import { useState, useRef, useEffect } from 'react'
import { validateNoteText } from '@/lib/validators'
import { useToast } from '@/components/providers/ToastProvider'
import { useSelectionCaptureReady } from '@/hooks/useSelectionCaptureReady'
import { HighlightedTextarea } from '@/components/ui/HighlightedTextarea/HighlightedTextarea'

interface NoteInputProps {
  onSend?: (noteText: string) => void
  className?: string
}

const MAX_CHARACTERS = 1000

export function NoteInput({ onSend, className = '' }: NoteInputProps) {
  const [noteText, setNoteText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { showToast } = useToast()
  // Gated until a full selection exists AND the selected asset is still within
  // its active period (present in the live taxonomy) — matches the record gate.
  const isGated = !useSelectionCaptureReady()

  const characterCount = noteText.length
  const remainingChars = MAX_CHARACTERS - characterCount
  const canSend =
    !isGated &&
    noteText.trim().length > 0 &&
    !error

  // Validate on change
  useEffect(() => {
    if (noteText.length > 0) {
      const validation = validateNoteText(noteText)
      if (!validation.valid) {
        setError(validation.errors[0] || null)
      } else {
        setError(null)
      }
    } else {
      setError(null)
    }
  }, [noteText])

  // Keyboard shortcuts: Cmd/Ctrl + Enter to send
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      if (canSend) {
        handleSend()
      }
    }
  }

  const handleSend = () => {
    if (!canSend) {
      if (isGated) {
        showToast('Please select an active asset before sending', 'warning')
      } else if (noteText.trim().length === 0) {
        showToast('Please enter a note before sending', 'warning')
      } else if (error) {
        showToast(error, 'error')
      }
      return
    }

    const validation = validateNoteText(noteText)
    if (!validation.valid) {
      setError(validation.errors[0] || null)
      showToast(validation.errors[0] || 'Note validation failed', 'error')
      return
    }

    onSend?.(noteText.trim())
    setNoteText('')
    setError(null)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isGated) return
    const value = e.target.value
    if (value.length <= MAX_CHARACTERS) {
      setNoteText(value)
    }
  }

  return (
    <div className={`w-full ${className}`}>
      <label htmlFor="note-input" className="block text-sm font-medium text-gray-300 mb-2">
        Type a Note
      </label>
      {isGated && (
        <p className="mb-2 text-xs text-gray-400">
          Select an active asset to enable notes.
        </p>
      )}
      <div className="relative">
        <HighlightedTextarea
          id="note-input"
          ref={textareaRef}
          value={noteText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            isGated
              ? 'Select an active asset to enable notes.'
              : 'Enter your note here... (Cmd/Ctrl + Enter to send)'
          }
          rows={4}
          maxLength={MAX_CHARACTERS}
          readOnly={isGated}
          dimmed={isGated}
          error={!!error}
          tabIndex={isGated ? -1 : 0}
          aria-label="Note input"
          aria-describedby={error ? 'note-error' : 'note-counter'}
          aria-invalid={!!error}
          aria-disabled={isGated}
        />
        {isGated && (
          <button
            type="button"
            tabIndex={-1}
            className="absolute inset-0 rounded-xl"
            aria-label="Complete selections to enable note input"
            onPointerDown={(e) => {
              e.preventDefault()
              showToast(
                'Please select an asset, indication, and key insight question before typing a note',
                'warning',
              )
            }}
            onClick={(e) => {
              e.preventDefault()
            }}
          />
        )}
      </div>

      {/* Counter + inline error sit BELOW the field in normal flow. A textarea's
          text scrolls under anything layered over it, so a counter floated inside
          the field gets overlapped by long notes (and padding won't stop it) —
          keep it out of the field's box entirely. */}
      <div className="mt-1 flex items-center justify-end gap-2">
        {error && (
          <span id="note-error" className="mr-auto text-xs text-red-400" role="alert">
            {error}
          </span>
        )}
        <span
          id="note-counter"
          className={`text-xs ${
            remainingChars < 50 ? 'text-yellow-400' : 'text-gray-400'
          }`}
        >
          {characterCount}/{MAX_CHARACTERS}
        </span>
      </div>

      {/* Send Button */}
      <div className="mt-3 flex justify-end">
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={`
            px-6 py-2 rounded-lg font-medium transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-navy
            disabled:opacity-50 disabled:cursor-not-allowed
            ${
              canSend
                ? 'bg-orange text-white hover:bg-orange-dark focus:ring-orange'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }
          `}
          aria-label="Send note"
        >
          Send Note
        </button>
      </div>

      {/* Helper text */}
      <p className="mt-2 text-xs text-gray-400">
        Use Cmd/Ctrl + Enter to send quickly
      </p>
    </div>
  )
}
